import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Zap, Bell, Check } from 'lucide-react';
import type { Product } from '../types';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { products as productsApi } from '../api/client';
import { getStrengthLabel, getStrengthColor } from '../data/products';
import { useTheme } from '../context/ThemeContext';

interface Props {
  product: Product;
  index?: number;
}

export default function ProductCard({ product, index = 0 }: Props) {
  const { addToCart, state } = useCart();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifyState, setNotifyState] = useState<'idle' | 'sending' | 'done'>('idle');
  const isOOS = !product.inStock;
  const stock = product.stockQuantity ?? 100;
  const cartQty = state.items.find((i) => i.product.id === product.id)?.quantity ?? 0;
  const atLimit = cartQty >= stock;

  const handleNotify = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setNotifyState('sending');
    try {
      await productsApi.notifyWhenAvailable(product.id, user.email);
      setNotifyState('done');
    } catch {
      setNotifyState('idle');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className={`group rounded-2xl border overflow-hidden transition-all duration-300 ${
        isOOS ? '' : 'hover:-translate-y-1'
      } ${
        isDark
          ? `bg-gray-900 border-gray-800 shadow-none ${isOOS ? '' : 'hover:shadow-xl hover:shadow-black/30'}`
          : `bg-white border-gray-100 shadow-sm ${isOOS ? '' : 'hover:shadow-xl hover:shadow-gray-200/60'}`
      }`}
    >
      {/* Image / Visual area */}
      <Link to={`/product/${product.id}`} className="block relative">
        <div
          className="h-56 flex items-center justify-center relative overflow-hidden"
          style={{ background: `linear-gradient(145deg, ${product.colorAccent}06, ${product.colorAccent}12, ${product.colorAccent}06)` }}
        >
          <img
            src={product.image}
            alt={`${product.name} nicotine pouches — ${product.pouchesPerCan} pouches per can`}
            className={`h-44 w-44 object-contain drop-shadow-lg transition-transform duration-500 ease-out ${isOOS ? '' : 'group-hover:scale-110'}`}
            loading="lazy"
          />

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.isNew && (
              <span className="px-2.5 py-1 bg-emerald-500 text-white text-[10px] font-bold rounded-md uppercase tracking-wider shadow-sm">
                New
              </span>
            )}
            {product.bestSeller && (
              <span className="px-2.5 py-1 bg-amber-500 text-white text-[10px] font-bold rounded-md uppercase tracking-wider shadow-sm">
                Bestseller
              </span>
            )}
            {product.originalPrice && (
              <span className="px-2.5 py-1 bg-rose-500 text-white text-[10px] font-bold rounded-md uppercase tracking-wider shadow-sm">
                {Math.round((1 - product.price / product.originalPrice) * 100)}% Off
              </span>
            )}
          </div>

          {/* Sold Out overlay banner — slim band at the bottom so the
              product image and badges remain visible */}
          {!product.inStock && (
            <div className="absolute inset-x-0 bottom-0 h-1/3 flex items-center justify-center bg-black/60 backdrop-blur-sm">
              <span className="text-white text-base font-extrabold uppercase tracking-widest">Sold Out</span>
            </div>
          )}

          {/* Low stock badge */}
          {product.inStock && stock > 0 && stock < 10 && (
            <div className="absolute bottom-3 left-3">
              <span className="px-2.5 py-1 bg-red-500 text-white text-[10px] font-bold rounded-md shadow-sm">
                Only {stock} left!
              </span>
            </div>
          )}

          {/* Strength badge */}
          <div className="absolute top-3 right-3">
            <div
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold text-white shadow-sm backdrop-blur-sm"
              style={{ backgroundColor: getStrengthColor(product.strength) + 'dd' }}
            >
              <Zap size={10} />
              {product.strength}{product.strengthUnit}
            </div>
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-4 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{
              color: product.colorAccent,
              backgroundColor: product.colorAccent + '12',
            }}
          >
            {product.brand}
          </span>
          <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {getStrengthLabel(product.strength)} · {product.format}
          </span>
        </div>

        <Link to={`/product/${product.id}`}>
          <h3 className={`font-semibold text-sm leading-snug group-hover:text-emerald-500 transition-colors line-clamp-2 mb-1.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {product.name}
          </h3>
        </Link>

        <p className={`text-xs line-clamp-1 mb-3 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {product.description}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={11}
                fill={i < Math.floor(product.rating) ? '#f59e0b' : isDark ? '#374151' : '#e5e7eb'}
                stroke={i < Math.floor(product.rating) ? '#f59e0b' : isDark ? '#374151' : '#e5e7eb'}
              />
            ))}
          </div>
          <span className={`text-xs font-semibold ml-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{product.rating}</span>
          <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>({product.reviewCount})</span>
        </div>

        {/* Price & CTA \u2014 flex-wrap drops the button to its own row on narrow
            cards where the price + Notify-me text would overflow side-by-side. */}
        <div className={`flex flex-wrap items-center gap-2 pt-2 border-t ${isDark ? 'border-gray-800' : 'border-gray-50'}`}>
          <div>
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {'\u20B9'}{product.price.toLocaleString('en-IN')}
            </span>
            {product.originalPrice && (
              <span className={`ml-1.5 text-xs line-through ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                {'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}
              </span>
            )}
          </div>
          {product.inStock ? (
            <button
              onClick={() => !atLimit && addToCart(product)}
              disabled={atLimit}
              className={`ml-auto shrink-0 p-2.5 rounded-xl transition-all duration-200 ${atLimit ? 'bg-gray-400 text-white cursor-not-allowed opacity-60' : 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/25 active:scale-90'}`}
              title={atLimit ? 'Stock limit reached' : 'Add to cart'}
            >
              <ShoppingCart size={16} />
            </button>
          ) : (
            <button
              onClick={handleNotify}
              disabled={notifyState !== 'idle'}
              title={!user ? 'Login to get notified' : notifyState === 'done' ? 'Subscribed!' : 'Notify me'}
              className={`ml-auto shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                notifyState === 'done'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-amber-500 text-white hover:bg-amber-600 hover:shadow-md hover:shadow-amber-500/30 active:scale-95'
              } disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100`}
            >
              {notifyState === 'done' ? <><Check size={12} /> Subscribed</> : notifyState === 'sending' ? <><div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" /> Saving</> : <><Bell size={12} /> Notify me</>}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
